/**
 * @module Experience/Services
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/*tslint:disable*/
import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { Base64URL, utf8ToUint8Array, createGUID } from '@support/shared/services/encoding-utils';
import { Observable, from } from 'rxjs';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { EndpointConfigInterface } from '@core/interfaces/endpoint-configuration.interface';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { WorkerRequestArgs } from '@core/worker/models/worker-request-args';
import { AppConfig } from '@root/app.config';
import { WEB_API_MAP } from '@core/api/proxies/web-api-endpoints';
import { WorkerUtils } from '@core/worker/models/utils';
const DefaultAlgorithms = {
  SIGN: {
    name: 'RSASSA-PKCS1-v1_5',
    // Must include the `hash` here so Edge does not error out.
    hash: { name: 'SHA-256' },
  },
};
@Injectable(
  { providedIn: 'root' }
)
export class CryptoSignatureService {
  constructor(
    private objectService: ObjectService,
    private stateStoreUtils: StateStoreUtilsService,
    private appConfig: AppConfig
  ) {}

  public generatePopToken(partialToken: string): Observable<ArrayBuffer> {
    // const partialToken = this.generatepartialToken();
    const key: CryptoKey = this.objectService.get(self, 'keyPair.privateKey');
    if (!WorkerUtils.isIE()) {
      return from(
        self.crypto.subtle.sign(
          DefaultAlgorithms.SIGN,
          key,
          utf8ToUint8Array(partialToken, self.btoa)
        )
      );
    } else {
      return from(this.signForIE(key, partialToken));
    }
  }

  public generatepartialToken(
    endpointConfig: EndpointConfigInterface,
    payload: string,
    method: string,
    headers: WorkerRequestArgs,
    pathParams: { [name: string]: string },
    param_url: string,
    queryParams: { [name: string]: string } = null
  ): string {
    const header = { alg: 'RS256', typ: 'JWT', kid: this.appConfig.getConfig('kid') };
    const authToken = this.stateStoreUtils.getAuthToken()
      ? this.stateStoreUtils.getAuthToken()
      : '';
    return (
      Base64URL.stringify(utf8ToUint8Array(JSON.stringify(header), self.btoa)) +
      '.' +
      Base64URL.stringify(
        utf8ToUint8Array(
          this.generateEhtsFormat(
            endpointConfig,
            payload,
            method,
            headers,
            authToken.trim(),
            pathParams,
            param_url,
            queryParams
          ),
          self.btoa
        )
      )
    );
  }

  private generateEhtsFormat(
    endpointConfig: EndpointConfigInterface,
    payload: string,
    method: string,
    headers: WorkerRequestArgs,
    authToken: string,
    pathParams: { [name: string]: string },
    paramUrl: string,
    queryParams: { [name: string]: string }
  ): string {
    // uri method body header authToken
    const ehtsKeyValueMap = new Map<string, string>();
    const url = endpointConfig.proxyName
      ? this.appConfig.getProxyUrl(endpointConfig.proxyName)
      : '';
    ehtsKeyValueMap.set(
      'Content-Type',
      headers.headers.get('content-type') ? headers.headers.get('content-type') : 'application/json'
    ); //content-type
    if (
      authToken &&
      authToken.length > 0 &&
      endpointConfig.url !== WEB_API_MAP.ANON_TOKEN_API_V6.url
    ) {
      if (!authToken.startsWith('Bearer')) {
        ehtsKeyValueMap.set('Authorization', 'Bearer ' + authToken);
      } else {
        ehtsKeyValueMap.set('Authorization', authToken);
      }
    }
    let primaryUri = endpointConfig.url;
    if (pathParams && Object.keys(pathParams).length > 0) {
      primaryUri = paramUrl;
    }
    paramUrl = encodeURI(paramUrl.trim());
    if (queryParams && Object.keys(queryParams).length > 0) {
      const querySuffix = this.generateQuerySuffix(queryParams);
      paramUrl = paramUrl + querySuffix;
    }
    const uri = endpointConfig.is_tag_proxy ? paramUrl : url.concat(paramUrl);
    ehtsKeyValueMap.set('uri', '/' + uri);
    ehtsKeyValueMap.set('http-method', method);
    if (payload) {
      ehtsKeyValueMap.set('body', payload);
    }
    if (headers.headers.get('activityid')) {
      ehtsKeyValueMap.set('activityId', headers.headers.get('activityid'));
    }
    if (headers.headers.get('channelid')) {
      ehtsKeyValueMap.set('channelId', headers.headers.get('channelid'));
    }
    if (headers.headers.get('interactionid')) {
      ehtsKeyValueMap.set('interactionId', headers.headers.get('interactionid'));
    }
    if (
      headers.headers.get('channelid') &&
      headers.headers.get('channelid').toLowerCase() === 'nrd' &&
      this.stateStoreUtils.getApplicationUserId()
    ) {
      ehtsKeyValueMap.set('applicationUserId', this.stateStoreUtils.getApplicationUserId());
    }
    let strEhts = '';
    let strToHash = '';
    ehtsKeyValueMap.forEach((value: string, key: string) => {
      if (strEhts.length > 0) {
        strEhts = strEhts + ';';
      }
      strEhts = strEhts + key;
      strToHash = strToHash + value;
    });
    console.log('ehts', strEhts);
    console.log('edts', strToHash);
    // validity logic
    const jti = createGUID();
    const edts = CryptoJS.SHA256(strToHash)
      .toString(CryptoJS.enc.Base64)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/\=+$/, '');

    const serverTimeDiff = Number(sessionStorage.getItem('serverLoadTime'));
    const iat = Number(Date.parse(new Date().toUTCString()) / 1000) + serverTimeDiff;
    const expTime =
      iat + (this.appConfig.getConfig('tokenExp') ? this.appConfig.getConfig('tokenExp') : 2 * 60);
    return JSON.stringify({
      edts: edts,
      v: '1',
      exp: expTime,
      ehts: strEhts,
      iat: iat,
      jti: jti,
    });
  }

  private generateQuerySuffix(queryParams: { [name: string]: string }): string {
    let suffixArray = [];
    for (const key in queryParams) {
      let suffixValue = key.toString() + '=' + queryParams[key];
      suffixArray.push(suffixValue);
    }
    return suffixArray.length > 0 ? '?' + suffixArray.join('&') : '';
  }

  private signForIE(key: CryptoKey, partialToken: string): Promise<any> {
    return new Promise((resolve: any, reject: any) => {
      //const winCrypto = this.objectService.get(self, 'msCrypto');
      const signObj = self['msCrypto'].subtle.sign(
        DefaultAlgorithms.SIGN,
        key,
        utf8ToUint8Array(partialToken, self.btoa)
      );
      signObj.oncomplete = function (event: any) {
        resolve(event.target.result);
      };
    });
  }
}
